import { FormatNumbers } from "./format-numbers";

/**
 * Os BLOCOS do tooltip quando dois lados dividem o mesmo mes do grafico.
 *
 * Na comparacao "todos os meses" e no orcamento, cada serie aparece duas vezes no mesmo `x` — uma
 * pelo periodo, outra pela comparacao (ou pelo orcado). O tooltip compartilhado listava as duas
 * com o mesmo nome e sem dizer qual era qual: "Entradas Totais" duas vezes, com valores diferentes.
 *
 * Quem e quem ja vem na serie: `stack` (`period` | `comparation`) e `metadata` (ano e cenario),
 * gravados pelo `HighchartsFormatSeriesAdapter`. Aqui so se agrupa por eles e se da um titulo.
 */

const ORDEM = ["period", "comparation"];

const PAPEL = {
    period: "Base",
    comparation: "Comparado"
};

/**
 * @param {Array<{ series: { userOptions: Object } }>} points os pontos do tooltip compartilhado
 * @param {{ budget?: boolean }} options
 * @returns {Array<{ label: string | null, points: Array }>} blocos na ordem Base, Comparado e,
 *   por ultimo, o que nao tem lado (objetivo) — sem titulo.
 */
export function groupTooltipPoints(points = [], { budget = false } = {}) {
    const porLado = new Map();
    for (const point of points) {
        const stack = point?.series?.userOptions?.stack;
        const lado = ORDEM.includes(stack) ? stack : null;
        if (!porLado.has(lado)) porLado.set(lado, []);
        porLado.get(lado).push(point);
    }

    const metadataDe = (lado) =>
        porLado.get(lado)?.[0]?.series?.userOptions?.metadata || {};

    // O cenario so entra no titulo quando DIFERE entre os lados: "Original" nos dois nao ajuda a
    // separar nada, so alonga a linha.
    const cenariosDiferem =
        metadataDe("period").scenario !== metadataDe("comparation").scenario;

    const tituloDe = (lado) => {
        const { year, scenario } = metadataDe(lado);
        // No orcamento o lado E o cenario: "Original" e "Orçamento".
        if (budget) return scenario || PAPEL[lado];
        return [PAPEL[lado], year, cenariosDiferem ? scenario : null]
            .filter(Boolean)
            .join(" · ");
    };

    const blocos = ORDEM.filter((lado) => porLado.has(lado)).map((lado) => ({
        label: tituloDe(lado),
        points: porLado.get(lado)
    }));
    if (porLado.has(null))
        blocos.push({ label: null, points: porLado.get(null) });
    return blocos;
}

const valorFormatado = (unit, value) => {
    if (unit === "percentage") {
        return (value / 100).toLocaleString("pt-BR", {
            style: "percent",
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
        });
    }
    return FormatNumbers.byUnit(unit, value);
};

/** Uma linha do tooltip: bolinha da cor da serie, nome e valor. */
export function tooltipLine(series, y) {
    return (
        `<div width="20" height="20" style="color:${series.color};">● </div>` +
        ` ${series.name}: <b>${valorFormatado(series.userOptions?.unit, y)}</b><br/>`
    );
}

/**
 * As opcoes de tooltip agrupado (Highcharts `shared`), para qualquer grafico em que os dois lados
 * dividem o mes: o da visualizacao (`getTooltip`) e o que a comparacao monta sozinha quando troca
 * rosca, pizza ou gauge por colunas (`HighchartsFormaterStrutureAdapter`). Um formatador so, para os
 * dois nao divergirem.
 */
export function groupedTooltipOptions({ budget = false } = {}) {
    return {
        shared: true,
        formatter: function () {
            const blocos = groupTooltipPoints(this.points, { budget })
                .map(
                    ({ label, points }, index) =>
                        // Respiro antes de cada bloco que nao e o primeiro: sem ele, o titulo do
                        // segundo cola na ultima linha do primeiro.
                        (index > 0 ? `<span style="font-size:6px"> </span><br/>` : "") +
                        (label
                            ? `<span style="font-size:11px;font-weight:bold;color:#4b5563">${label}</span><br/>`
                            : "") +
                        points.map((p) => tooltipLine(p.series, p.y)).join("")
                )
                .join("");
            return `<b>${this.points?.[0]?.key ?? this.x}</b><br>${blocos}`;
        }
    };
}
